import { Check, Copy } from "lucide-react";
import React, { ReactNode, useMemo, useState } from "react";

import { Box, useTheme } from "@mui/material";

import { IgTooltip } from "./IgTooltip";
import { IgTypography, IgTypographyProps } from "./IgTypography";

interface CopyTextProps {
  text: string;
  customCopyText?: string | (() => string);
  typographyProps?: IgTypographyProps;
  stopPropagation?: boolean;
  showIcon?: boolean;
  onDarkBackground?: boolean;
  copiedTooltip?: (text: string) => string;
}

export const IgCopyTextTypography = ({
  text,
  typographyProps,
  customCopyText,
  stopPropagation = false,
  showIcon = false,
  onDarkBackground = false,
  copiedTooltip,
}: CopyTextProps) => {
  const { sx, ...props } = typographyProps ?? {};
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const copyHoverBackgroundColor = onDarkBackground
    ? theme.igBackgroundColors.interactive.dark.hover
    : theme.igBackgroundColors.accent;
  const copyIconColor = onDarkBackground
    ? theme.igIconColors.dark
    : theme.palette.text.secondary;

  const onCopy = async (inputText: string) => {
    await navigator.clipboard.writeText(inputText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  const copyText = useMemo(() => {
    const _copyText =
      typeof customCopyText === "function"
        ? customCopyText()
        : (customCopyText ?? text);
    return _copyText;
  }, [customCopyText, text]);

  const content = (
    <Box
      display="inline-flex"
      alignItems="center"
      gap={0.5}
      onClick={(event) => {
        if (stopPropagation) {
          event.stopPropagation();
        }
        void onCopy(copyText);
      }}
      sx={{
        cursor: "pointer",
        padding: "1px",
        borderRadius: "5px",
        "&:hover": {
          backgroundColor: copyHoverBackgroundColor,
        },
      }}
    >
      <IgTypography sx={sx} {...props}>
        {text}
      </IgTypography>
      {showIcon ? (
        copied ? (
          <Box sx={{ ml: 1 }} component={"span"}>
            <Check size={14} color={theme.palette.success.main} />
          </Box>
        ) : (
          <Box sx={{ ml: 1 }} component="span">
            <Copy size={14} color={copyIconColor} />
          </Box>
        )
      ) : null}
    </Box>
  );

  return copiedTooltip ? (
    <IgTooltip title={copiedTooltip(copyText)} open={copied} placement="top">
      {content}
    </IgTooltip>
  ) : (
    content
  );
};
